import React from "react";
import { useI18n } from "@/i18n/I18nContext";
import { reportClientError } from "@/lib/reportError";

// Un chunk de una versión anterior que ya no está en el servidor (tras un
// despliegue): se arregla recargando.
const isChunkError = (error) =>
  error?.name === "ChunkLoadError" || /Loading (CSS )?chunk/.test(error?.message || "");

const reloadOnceForNewVersion = () => {
  try {
    if (sessionStorage.getItem("colygon_chunk_reload")) return;
    sessionStorage.setItem("colygon_chunk_reload", "true");
  } catch { /* sin sessionStorage: se recarga igual */ }
  window.location.reload();
};

const Notice = ({ fullScreen, testid, title, desc, children }) => (
  <div
    data-testid={testid}
    className={`flex flex-col items-center justify-center p-6 text-center ${fullScreen ? "min-h-screen bg-gray-50" : "min-h-[60vh]"}`}
  >
    <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full border border-gray-100">
      <h1 className="text-2xl font-extrabold text-colygon-darkest mb-4">{title}</h1>
      <p className="text-gray-600 mb-8">{desc}</p>
      <div className="flex flex-col gap-3">{children}</div>
    </div>
  </div>
);

const ReloadButton = ({ label }) => (
  <button
    type="button"
    onClick={() => window.location.reload()}
    className="w-full bg-colygon hover:bg-colygon/90 text-white font-bold py-3 px-4 rounded-xl transition-colors shadow-lg"
  >
    {label}
  </button>
);

const UpdateNotice = ({ fullScreen }) => {
  const { t } = useI18n();
  return (
    <Notice fullScreen={fullScreen} testid="app-update-notice" title={t("app_update.title")} desc={t("app_update.desc")}>
      <ReloadButton label={t("app_update.reload")} />
    </Notice>
  );
};

const ErrorNotice = ({ fullScreen }) => {
  const { t } = useI18n();
  return (
    <Notice fullScreen={fullScreen} testid="app-error-notice" title={t("app_error.title")} desc={t("app_error.desc")}>
      <ReloadButton label={t("app_error.retry")} />
      <a href="/" className="w-full py-3 px-4 rounded-xl font-bold text-colygon-dark border border-slate-200 hover:bg-slate-50 transition-colors">
        {t("app_error.home")}
      </a>
    </Notice>
  );
};

// Si algo falla al pintar, en vez de quedarse la pantalla en blanco:
// - un chunk de la versión anterior → aviso de versión nueva y se recarga
//   sola (una vez por sesión);
// - cualquier otro fallo → "algo ha fallado", con reintentar e ir al inicio.
// Con resetKey (la ruta, en Layout) el fallo se queda en esa pantalla: el
// menú sigue funcionando y al ir a otra se vuelve a pintar.
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error al pintar la pantalla:", error, errorInfo);
    // Versión nueva desplegada: se recarga. Cualquier otro fallo, al equipo.
    if (isChunkError(error)) reloadOnceForNewVersion();
    else reportClientError(error, errorInfo);
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return isChunkError(error)
      ? <UpdateNotice fullScreen={this.props.fullScreen} />
      : <ErrorNotice fullScreen={this.props.fullScreen} />;
  }
}
